import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import Close from '@mui/icons-material/Close'
import Send from '@mui/icons-material/Send'
import Stop from '@mui/icons-material/Stop'
import ContentCopy from '@mui/icons-material/ContentCopy'
import AutoAwesome from '@mui/icons-material/AutoAwesome'
import ViewSidebarOutlined from '@mui/icons-material/ViewSidebarOutlined'
import ArrowBack from '@mui/icons-material/ArrowBack'
import OpenInFull from '@mui/icons-material/OpenInFull'
import { useLocation, useNavigate } from 'react-router-dom'
import { inventoryV3Href } from '@/views/SuperAdmin/InventoryV3/inventoryV3Route'
import { useTheme } from '@mui/material/styles'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { useAuth } from 'hooks/auth/useAuth'
import { useAssistant } from './AssistantContext'
import { AssistantMarkdown } from './AssistantMarkdown'
import { AssistantToolStatus } from './AssistantToolStatus'
import { AssistantToolEditorButton } from './AssistantToolEditor'
import { AssistantMessageExtras, messageHasWork } from './AssistantMessageExtras'
import { DemoPreviewSample } from './AssistantPreviewCard'
import { AssistantWorkStage, WORKSPACE_STARTERS, promptBefore, messageOnStage, type StageEntry } from './AssistantWorkStage'
import {
  AssistantSettingsPopover,
} from './AssistantSettingsPopover'
import {
  useAssistantChat,
  type AssistantChartType,
  type AssistantChatMessage,
  type AssistantScanNote,
  type ProposedAction,
} from './useAssistantChat'
import { type AssistantToolPrefs } from './assistantToolPrefs'
import { resolveAssistantPath, assistantPathToLocation } from './portalPaths'
import type { AssistantRouteTable } from './assistantRoutes'
import { ASSISTANT_WORKSPACE_PATH } from './AssistantWorkspaceRoute'
import { ASSISTANT_DEMO_EVENT, type AssistantDemoDetail } from './PortalAssistantOnboarding'
import { useAssistantSettings } from './useAssistantSettings'
import { AssistantProfileDialog } from './AssistantProfileDialog'
import {
  subscribeAssistantProfiles,
  createAssistantRoutine,
  runAssistantRoutineNow,
  type AssistantProfileSummary,
  type AssistantReportTopic,
  type AssistantRoutineKind,
  type AssistantRoutinePeriod,
  type AssistantRoutineSchedule,
} from './assistantProfiles'
import { AssistantHistoryPane } from './AssistantHistoryPane'
import { AssistantShareButton } from './AssistantShareButton'
import { useTimesheet } from 'hooks/timesheet'
import { useInventoryWrite } from 'hooks/inventory/useInventoryWrite'
import { useLeads } from 'hooks/admin/useLeads'
import { useAdminListBuilder } from 'hooks/useAdminListBuilder'
import { useMarketingCampaigns } from 'hooks/contacts/useMarketingCampaigns'
import type { MarketingCampaign } from '@otw/models/leads/marketingCampaign'
import type { LeadStatus } from '@otw/models/leads/lead'
import { canonicalVehicleTier, vehicleTierRaiseError } from '@otw/models/inventory'
import { doc, getDoc } from 'firebase/firestore'
import { db } from 'config/firebase'
import { DateTime } from 'luxon'
import { enqueueSnackbar } from 'notistack'
import {
  APPBAR_HEIGHT_DESKTOP,
  CHROME_GAP,
  CHROME_RADIUS,
  SAFE_AREA_BOTTOM,
  SAFE_AREA_TOP,
  TOUCH_MIN,
} from 'core/theme.tokens'

type RoutineComposerDraft = {
  prompt: string
  period: AssistantRoutinePeriod
  schedule: AssistantRoutineSchedule
  kind: AssistantRoutineKind
  topic: AssistantReportTopic
}

function routineComposerDraft(messages: AssistantChatMessage[], replyIndex: number): RoutineComposerDraft {
  const start = Math.max(0, replyIndex - 5)
  const lines = messages.slice(start, replyIndex + 1).flatMap(item => {
    const text = item.content.trim()
    if (!text) return []
    return [`${item.role === 'user' ? 'User' : 'Assistant'}: ${text}`]
  })
  const context = lines.join('\n\n')
  const prompt = `Make a routine from this conversation.\n\n${context}`.slice(0, 4000)
  const asked = messages
    .slice(start, replyIndex + 1)
    .filter(item => item.role === 'user')
    .map(item => item.content)
    .join('\n')
    .toLowerCase()
  let schedule: AssistantRoutineSchedule = 'weekdays'
  if (/\bevery day\b|\bdaily\b|\beach day\b/.test(asked)) schedule = 'daily'
  else if (/\bweekdays?\b/.test(asked)) schedule = 'weekdays'
  else if (/\bweekly\b|\bevery week\b|\beach week\b/.test(asked)) schedule = 'weekly'
  else if (/\bjust once\b|\bone time\b|\brun once\b/.test(asked)) schedule = 'once'

  let period: AssistantRoutinePeriod = 'last_24_hours'
  if (/\blast 24|\bpast 24|\b24 hours?\b/.test(asked)) period = 'last_24_hours'
  else if (/\byesterday\b|\bprevious day\b|\bprior day\b/.test(asked)) period = 'previous_day'
  else if (/\btoday\b/.test(asked)) period = 'today'
  else if (/\blast 7 days\b|\bpast 7 days\b/.test(asked)) period = 'last_7_days'
  else if (/\blast week\b|\bprevious week\b|\bprior week\b/.test(asked)) period = 'previous_week'
  else if (/\bthis week\b/.test(asked)) period = 'this_week'
  else if (/\blast 30 days\b|\bpast 30 days\b/.test(asked)) period = 'last_30_days'
  else if (/\blast month\b|\bprevious month\b|\bprior month\b/.test(asked)) period = 'previous_month'
  else if (/\bthis month\b/.test(asked)) period = 'this_month'
  else if (/\blast quarter\b|\bprevious quarter\b/.test(asked)) period = 'previous_quarter'
  else if (/\bthis quarter\b/.test(asked)) period = 'this_quarter'
  else if (/\blast year\b|\bprevious year\b/.test(asked)) period = 'previous_year'
  else if (/\bthis year\b/.test(asked)) period = 'this_year'

  const kind: AssistantRoutineKind = /\breport\b/.test(asked) ? 'report' : 'email'
  let topic: AssistantReportTopic = 'sales'
  if (/\binventory\b|\btrucks added\b/.test(asked) && !/\bsales\b|\bsold\b/.test(asked)) topic = 'inventory'
  else if (/\binventory\b/.test(asked) && /\bsales\b|\bsold\b/.test(asked)) topic = 'custom'

  return { prompt, period, schedule, kind, topic }
}

const SUGGESTIONS = [
  'Gray Book price for stock…',
  'Find truck by stock number',
  'Top sales this month',
  'Which shop should I send stock…',
  'Pending sales sitting too long',
  'Spend by category',
]

const HISTORY_WIDTH = 280
const ASSISTANT_MIN_WIDTH = 320
const ASSISTANT_MAX_WIDTH = 960
const ASSISTANT_DEFAULT_WIDTH = 480
/** Wide enough for fuller markdown / action cards. */
const ASSISTANT_WIDE_THRESHOLD = 640
const HISTORY_TAB_STORAGE_KEY = 'otw.assistant.historyTab:v1'
const HISTORY_OPEN_STORAGE_KEY = 'otw.assistant.historyOpen:v1'
const WIDTH_STORAGE_KEY = 'otw.assistant.widthPx'
const WORKSPACE_CHAT_KEY = 'otw.assistant.workspaceChatPx'
const PINNED_CHARTS_KEY = 'otw.assistant.pinnedCharts'
const STAGE_PINS_KEY = 'otw.assistant.stagePins'
const STAGE_CLEARED_KEY = 'otw.assistant.stageCleared'
const WORKSPACE_CHAT_MIN = 480
const WORKSPACE_CHAT_DEFAULT = 720
const WORKSPACE_STAGE_MIN = 340
const LEGACY_WIDTH_INDEX_KEY = 'otw.assistant.widthIndex'
const LEGACY_WIDTHS = [360, 480, 640, 800] as const

function clampAssistantWidth(px: number): number {
  const maxByViewport =
    typeof window !== 'undefined'
      ? Math.max(ASSISTANT_MIN_WIDTH, window.innerWidth - CHROME_GAP * 2)
      : ASSISTANT_MAX_WIDTH
  const max = Math.min(ASSISTANT_MAX_WIDTH, maxByViewport)
  return Math.min(max, Math.max(ASSISTANT_MIN_WIDTH, Math.round(px)))
}

type HistoryTab = 'chats' | 'assistants'

function readStoredHistoryTab(): HistoryTab | null {
  try {
    const raw = sessionStorage.getItem(HISTORY_TAB_STORAGE_KEY)
    if (raw === 'chats' || raw === 'assistants') return raw
  } catch {
    // ignore
  }
  return null
}

function readStoredHistoryOpen(): boolean {
  try {
    return sessionStorage.getItem(HISTORY_OPEN_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function readStoredWidth(): number {
  try {
    const raw = sessionStorage.getItem(WIDTH_STORAGE_KEY)
    if (raw != null) {
      const n = Number(raw)
      if (Number.isFinite(n)) return clampAssistantWidth(n)
    }
    const legacy = sessionStorage.getItem(LEGACY_WIDTH_INDEX_KEY)
    if (legacy != null) {
      const idx = Math.min(LEGACY_WIDTHS.length - 1, Math.max(0, Math.trunc(Number(legacy))))
      if (Number.isFinite(idx)) return clampAssistantWidth(LEGACY_WIDTHS[idx] ?? ASSISTANT_DEFAULT_WIDTH)
    }
  } catch {
    // ignore
  }
  return ASSISTANT_DEFAULT_WIDTH
}

function clampWorkspaceChat(px: number, historyOpen: boolean): number {
  const viewport = typeof window !== 'undefined' ? window.innerWidth : 1440
  const history = historyOpen ? HISTORY_WIDTH : 0
  const max = Math.max(WORKSPACE_CHAT_MIN, viewport - history - WORKSPACE_STAGE_MIN - 12)
  return Math.min(max, Math.max(WORKSPACE_CHAT_MIN, Math.round(px)))
}

function readWorkspaceChat(): number {
  try {
    const n = Number(sessionStorage.getItem(WORKSPACE_CHAT_KEY))
    if (Number.isFinite(n) && n > 0) return n
  } catch {
    // ignore
  }
  return WORKSPACE_CHAT_DEFAULT
}

function chartPinId(messageId: string, index: number): string {
  return `${messageId}:${index}`
}

function readStoredIds(storageKey: string, chatId: string | null): string[] {
  if (!chatId) return []
  try {
    const raw = sessionStorage.getItem(storageKey)
    if (!raw) return []
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const list = parsed[chatId]
    return Array.isArray(list) ? list.filter((item): item is string => typeof item === 'string') : []
  } catch {
    return []
  }
}

function writeStoredIds(storageKey: string, chatId: string | null, ids: string[]): void {
  if (!chatId) return
  try {
    const raw = sessionStorage.getItem(storageKey)
    const parsed = raw ? (JSON.parse(raw) as Record<string, string[]>) : {}
    parsed[chatId] = ids
    sessionStorage.setItem(storageKey, JSON.stringify(parsed))
  } catch {
    // ignore
  }
}

function readPinnedCharts(chatId: string | null): string[] {
  return readStoredIds(PINNED_CHARTS_KEY, chatId)
}

function writePinnedCharts(chatId: string | null, ids: string[]): void {
  writeStoredIds(PINNED_CHARTS_KEY, chatId, ids)
}

function formatScanDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) return iso
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function scanDebugLines(note: AssistantScanNote): string[] {
  const count = note.maxRecords.toLocaleString('en-US')
  const range = `Date range: ${formatScanDate(note.startDate)} through ${formatScanDate(note.endDate)}.`
  const lines = [
    note.hitCap
      ? `Only ${count} records were searched for this. ${range}`
      : `Up to ${count} records were searched for this. ${range}`,
  ]
  if (note.windowCapped) lines.push('The date range was shortened to 24 months.')
  if (note.scanCapped) lines.push('The record cap was limited to 5,000.')
  if (note.hitCap) lines.push('Older matches outside that scan may be missing.')
  return lines
}

export const AssistantDrawer: React.FC<{ routes: AssistantRouteTable }> = ({ routes }) => {
  const assistant = useAssistant()
  const theme = useTheme()
  const isMobile = useIsMobile()
  const location = useLocation()
  const navigate = useNavigate()
  const { startTimer } = useTimesheet()
  const { updateDocumentAsync } = useInventoryWrite('master_inventory', {
    dontShowUpdates: true,
  })
  const { createLead, updateLead } = useLeads({ fetchList: false })
  const { createList } = useAdminListBuilder()
  const { createCampaign, sendCampaign } = useMarketingCampaigns()
  const {
    effort,
    setEffort,
    model,
    setModel,
    closeAfterNavigate,
    setCloseAfterNavigate,
    learnFromChats,
    setLearnFromChats,
    toolPrefs,
    setToolPrefs,
    debugMode,
    setDebugMode,
  } = useAssistantSettings()
  const { currentUser, userInfo } = useAuth()
  const assistantUid = currentUser?.uid ?? null
  const [historyTab, setHistoryTab] = useState<HistoryTab>(
    () => readStoredHistoryTab() ?? 'chats',
  )
  const [profiles, setProfiles] = useState<AssistantProfileSummary[]>([])
  const [profilesLoading, setProfilesLoading] = useState(false)
  const [profilesError, setProfilesError] = useState<string | null>(null)
  const defaultedAssistantsTabRef = useRef(false)
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null)
  const [profileDialog, setProfileDialog] = useState<string | null>(null)
  const [routineComposer, setRoutineComposer] = useState(false)
  const [routineDraft, setRoutineDraft] = useState<RoutineComposerDraft | null>(null)
  const activeProfile = profiles.find(profile => profile.id === activeProfileId) ?? null
  const authorName =
    [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(' ').trim() ||
    userInfo?.displayName ||
    'Admin'
  const [workspace, setWorkspace] = useState(
    () => window.location.pathname === ASSISTANT_WORKSPACE_PATH,
  )
  const [phonePane, setPhonePane] = useState<'work' | 'chat'>('work')
  const [workHidden, setWorkHidden] = useState(false)
  const stickChatRef = useRef(false)
  const assistantSubscriptionsEnabled = Boolean(assistant?.isOpen) || workspace
  const { messages, threads, chatId, streaming, streamingChatIds, toolSteps, threadsLoading, transcriptLoading, error, canRetry, send, retry, cancel, clear, newChat, openChat, renameChat, deleteChat, dismissError } =
    useAssistantChat({
      currentPath: location.pathname,
      effort,
      model,
      workspace,
      toolPrefs,
      assistantId: activeProfile?.id ?? null,
      transcriptOwnerId: activeProfile?.ownerId ?? null,
      authorName,
      subscriptionsEnabled: assistantSubscriptionsEnabled,
    })
  const [historyOpen, setHistoryOpen] = useState(readStoredHistoryOpen)

  useEffect(() => {
    try {
      sessionStorage.setItem(HISTORY_TAB_STORAGE_KEY, historyTab)
    } catch {
      // ignore
    }
  }, [historyTab])

  useEffect(() => {
    try {
      sessionStorage.setItem(HISTORY_OPEN_STORAGE_KEY, historyOpen ? '1' : '0')
    } catch {
      // ignore
    }
  }, [historyOpen])

  useEffect(() => {
    if (!assistantUid || !assistantSubscriptionsEnabled) {
      setProfiles([])
      setProfilesLoading(false)
      setProfilesError(null)
      return
    }
    setProfilesLoading(true)
    setProfilesError(null)
    return subscribeAssistantProfiles(
      assistantUid,
      next => {
        setProfiles(next)
        setProfilesLoading(false)
        setProfilesError(null)
      },
      () => {
        setProfiles([])
        setProfilesLoading(false)
        setProfilesError('Could not load assistants')
      },
    )
  }, [assistantUid, assistantSubscriptionsEnabled])

  useEffect(() => {
    if (profilesLoading || defaultedAssistantsTabRef.current) return
    if (readStoredHistoryTab() != null) {
      defaultedAssistantsTabRef.current = true
      return
    }
    if (profiles.length > 0) {
      defaultedAssistantsTabRef.current = true
      setHistoryTab('assistants')
    }
  }, [profiles.length, profilesLoading])

  useEffect(() => {
    const match = profiles.find(profile => profile.chatId === chatId)
    if (match) {
      if (match.id !== activeProfileId) setActiveProfileId(match.id)
      setHistoryTab('assistants')
    }
  }, [activeProfileId, chatId, profiles])
  const [demoSample, setDemoSample] = useState<'thread' | 'stage' | false>(false)
  const [draft, setDraft] = useState('')
  const [dismissedActions, setDismissedActions] = useState<Set<string>>(new Set())
  const [busyActionKey, setBusyActionKey] = useState<string | null>(null)
  const [assistantWidth, setAssistantWidth] = useState(readStoredWidth)
  const [workspaceChatPx, setWorkspaceChatPx] = useState(() =>
    clampWorkspaceChat(readWorkspaceChat(), false),
  )
  const [pinnedChartIds, setPinnedChartIds] = useState<string[]>([])
  const [pinnedStageIds, setPinnedStageIds] = useState<string[]>([])
  const [clearedStageIds, setClearedStageIds] = useState<string[]>([])
  const [isResizing, setIsResizing] = useState(false)
  const [splitResizing, setSplitResizing] = useState(false)
  const bottomRef = useRef<HTMLDivElement | null>(null)
  const dragStartXRef = useRef(0)
  const dragStartWidthRef = useRef(ASSISTANT_DEFAULT_WIDTH)
  const splitStartXRef = useRef(0)
  const splitStartWidthRef = useRef(WORKSPACE_CHAT_DEFAULT)
  const portalReturnRef = useRef('/admin')

  const isWideLayout =
    !isMobile &&
    (workspace
      ? workspaceChatPx >= ASSISTANT_WIDE_THRESHOLD
      : assistantWidth >= ASSISTANT_WIDE_THRESHOLD)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streaming, toolSteps])

  useEffect(() => {
    if (!assistant?.isOpen || !assistant.seed) return
    const seed = assistant.seed
    assistant.clearSeed()
    void send(seed)
  }, [assistant, send])

  useEffect(() => {
    try {
      sessionStorage.setItem(WIDTH_STORAGE_KEY, String(assistantWidth))
    } catch {
      // ignore
    }
  }, [assistantWidth])

  useEffect(() => {
    if (!assistant) return
    if (location.pathname === ASSISTANT_WORKSPACE_PATH) {
      setWorkspace(true)
      if (!assistant.isOpen) assistant.openAssistant()
      return
    }
    setWorkspace(false)
  }, [assistant, location.pathname])

  const workspacePathRef = useRef(location.pathname === ASSISTANT_WORKSPACE_PATH)
  useEffect(() => {
    const onWorkspace = location.pathname === ASSISTANT_WORKSPACE_PATH
    if (onWorkspace && !workspacePathRef.current) setWorkHidden(false)
    if (!onWorkspace) setWorkHidden(false)
    workspacePathRef.current = onWorkspace
  }, [location.pathname])

  useEffect(() => {
    setPinnedChartIds(readPinnedCharts(chatId))
    setPinnedStageIds(readStoredIds(STAGE_PINS_KEY, chatId))
    setClearedStageIds(readStoredIds(STAGE_CLEARED_KEY, chatId))
  }, [chatId])

  useEffect(() => {
    setWorkspaceChatPx(px => clampWorkspaceChat(px, historyOpen))
  }, [historyOpen])

  const latestAssistant = [...messages].reverse().find(message => message.role === 'assistant') ?? null
  const latestHasWork = latestAssistant ? messageHasWork(latestAssistant) : false

  const stageLists = useMemo(() => {
    const cleared = new Set(clearedStageIds)
    const pinnedSet = new Set(pinnedStageIds)
    const entries: StageEntry[] = []
    messages.forEach((message, index) => {
      if (!messageOnStage(message)) return
      const pinned = pinnedSet.has(message.id)
      if (cleared.has(message.id) && !pinned) return
      entries.push({
        message,
        prompt: promptBefore(messages, index),
        pinned,
      })
    })
    const pinnedOrder = new Map(pinnedStageIds.map((id, index) => [id, index]))
    const pinned = entries
      .filter(entry => entry.pinned)
      .sort((a, b) => (pinnedOrder.get(a.message.id) ?? 0) - (pinnedOrder.get(b.message.id) ?? 0))
    const history = entries.filter(entry => !entry.pinned)
    return { pinned, history }
  }, [clearedStageIds, messages, pinnedStageIds])

  const forgetStageMessage = useCallback(
    (messageId: string) => {
      setClearedStageIds(prev => {
        const next = prev.includes(messageId) ? prev : [...prev, messageId]
        writeStoredIds(STAGE_CLEARED_KEY, chatId, next)
        return next
      })
      setPinnedStageIds(prev => {
        const next = prev.filter(id => id !== messageId)
        writeStoredIds(STAGE_PINS_KEY, chatId, next)
        return next
      })
      setPinnedChartIds(prev => {
        const next = prev.filter(id => !id.startsWith(`${messageId}:`))
        writePinnedCharts(chatId, next)
        return next
      })
    },
    [chatId],
  )

  const toggleStagePin = useCallback(
    (messageId: string) => {
      setPinnedStageIds(prev => {
        const next = prev.includes(messageId)
          ? prev.filter(id => id !== messageId)
          : [messageId, ...prev]
        writeStoredIds(STAGE_PINS_KEY, chatId, next)
        return next
      })
      setClearedStageIds(prev => {
        if (!prev.includes(messageId)) return prev
        const next = prev.filter(id => id !== messageId)
        writeStoredIds(STAGE_CLEARED_KEY, chatId, next)
        return next
      })
    },
    [chatId],
  )

  const clearStageHistory = useCallback(() => {
    const drop = stageLists.history.map(entry => entry.message.id)
    if (!drop.length) return
    setClearedStageIds(prev => {
      const next = [...prev]
      for (const id of drop) {
        if (!next.includes(id)) next.push(id)
      }
      writeStoredIds(STAGE_CLEARED_KEY, chatId, next)
      return next
    })
  }, [chatId, stageLists.history])

  const toggleChartPin = useCallback(
    (id: string) => {
      setPinnedChartIds(prev => {
        const next = prev.includes(id) ? prev.filter(item => item !== id) : [id, ...prev]
        writePinnedCharts(chatId, next)
        return next
      })
    },
    [chatId],
  )

  useEffect(() => {
    stickChatRef.current = false
  }, [latestAssistant?.id])

  useEffect(() => {
    if (!workspace || !isMobile || stickChatRef.current) return
    if (latestHasWork) setPhonePane('work')
  }, [workspace, isMobile, latestHasWork, latestAssistant?.id])

  useEffect(() => {
    if (isMobile) return
    const onResize = () => {
      setAssistantWidth(w => clampAssistantWidth(w))
      setWorkspaceChatPx(px => clampWorkspaceChat(px, historyOpen))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [isMobile, historyOpen])

  useEffect(() => {
    if (!isResizing && !splitResizing) return
    const previousCursor = document.body.style.cursor
    const previousUserSelect = document.body.style.userSelect
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    return () => {
      document.body.style.cursor = previousCursor
      document.body.style.userSelect = previousUserSelect
    }
  }, [isResizing, splitResizing])

  const handleResizePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (isMobile) return
      event.preventDefault()
      event.stopPropagation()
      dragStartXRef.current = event.clientX
      dragStartWidthRef.current = assistantWidth
      setIsResizing(true)
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    [assistantWidth, isMobile],
  )

  const handleResizePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isResizing) return
      // Drawer is anchored right — drag left (negative delta) widens.
      const delta = dragStartXRef.current - event.clientX
      setAssistantWidth(clampAssistantWidth(dragStartWidthRef.current + delta))
    },
    [isResizing],
  )

  const endResize = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isResizing) return
    setIsResizing(false)
    try {
      event.currentTarget.releasePointerCapture(event.pointerId)
    } catch {
      // already released
    }
  }, [isResizing])

  const handleSplitPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (isMobile) return
      event.preventDefault()
      event.stopPropagation()
      splitStartXRef.current = event.clientX
      splitStartWidthRef.current = workspaceChatPx
      setSplitResizing(true)
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    [isMobile, workspaceChatPx],
  )

  const handleSplitPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!splitResizing) return
      // Chat is on the left. Drag right to widen the chat; drag left to widen the charts.
      const delta = event.clientX - splitStartXRef.current
      const next = clampWorkspaceChat(splitStartWidthRef.current + delta, historyOpen)
      setWorkspaceChatPx(next)
      try {
        sessionStorage.setItem(WORKSPACE_CHAT_KEY, String(next))
      } catch {
        // ignore
      }
    },
    [historyOpen, splitResizing],
  )

  const endSplitResize = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!splitResizing) return
    setSplitResizing(false)
    try {
      event.currentTarget.releasePointerCapture(event.pointerId)
    } catch {
      // already released
    }
  }, [splitResizing])

  const openWorkspace = useCallback(() => {
    if (location.pathname !== ASSISTANT_WORKSPACE_PATH) {
      portalReturnRef.current = `${location.pathname}${location.search}${location.hash}`
    }
    navigate(ASSISTANT_WORKSPACE_PATH)
  }, [location.hash, location.pathname, location.search, navigate])

  const leaveToPortal = useCallback(() => {
    const back = portalReturnRef.current
    setWorkspace(false)
    assistant?.closeAssistant()
    navigate(back && back !== ASSISTANT_WORKSPACE_PATH ? back : '/admin')
  }, [assistant, navigate])

  const returnFromWorkspace = useCallback(() => {
    const back = portalReturnRef.current
    setWorkspace(false)
    navigate(back && back !== ASSISTANT_WORKSPACE_PATH ? back : '/admin')
  }, [navigate])

  useEffect(() => {
    const onDemo = (event: Event) => {
      const detail = (event as CustomEvent<AssistantDemoDetail>).detail
      if (!detail) return
      if (detail.exit || detail.active === false) {
        setDemoSample(false)
      }
      if (detail.samplePreview === 'thread' || detail.samplePreview === 'stage' || detail.samplePreview === false) {
        setDemoSample(detail.samplePreview)
      }
      if (detail.exit) {
        setHistoryOpen(false)
        if (window.location.pathname === ASSISTANT_WORKSPACE_PATH) leaveToPortal()
        else assistant?.closeAssistant()
        return
      }
      if (detail.active != null && detail.history == null && detail.workspace == null && !detail.phonePane) {
        return
      }
      if (typeof detail.history === 'boolean') setHistoryOpen(detail.history)
      if (detail.phonePane) {
        stickChatRef.current = detail.phonePane === 'chat'
        setPhonePane(detail.phonePane)
      }
      if (detail.workspace) {
        if (window.location.pathname !== ASSISTANT_WORKSPACE_PATH) openWorkspace()
      } else if (window.location.pathname === ASSISTANT_WORKSPACE_PATH) {
        returnFromWorkspace()
      }
    }
    window.addEventListener(ASSISTANT_DEMO_EVENT, onDemo)
    return () => window.removeEventListener(ASSISTANT_DEMO_EVENT, onDemo)
  }, [assistant, leaveToPortal, openWorkspace, returnFromWorkspace])

  if (!assistant) return null

  const goToPath = (path: string) => {
    const resolved = resolveAssistantPath(path, routes)
    if (!resolved) return
    navigate(assistantPathToLocation(resolved))
    if (workspace || closeAfterNavigate) {
      if (workspace) setWorkspace(false)
      assistant.closeAssistant()
    }
  }

  const handleSend = () => {
    if (streaming) return
    const value = draft
    setDraft('')
    void send(value)
  }

  const handleToolPrefsChange = (next: AssistantToolPrefs) => {
    setToolPrefs(next)
  }

  const handleChartTypePick = (type: AssistantChartType) => {
    if (streaming) return
    void send(`Please use a ${type} chart.`)
  }

  const showWorkStage = workspace && !workHidden && (!isMobile || phonePane === 'work')
  const showThread = !workspace || !isMobile || phonePane === 'chat'
  const hideStageExtras = showWorkStage

  const handleComposerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      if (!streaming) handleSend()
    }
  }

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      enqueueSnackbar('Copied', { variant: 'success' })
    } catch {
      enqueueSnackbar('Could not copy', { variant: 'error' })
    }
  }

  const actionKey = (messageId: string, action: ProposedAction, index: number) =>
    `${messageId}:${action.type}:${index}`

  const handleConfirm = async (action: ProposedAction, messageId: string, index: number) => {
    const key = actionKey(messageId, action, index)
    setBusyActionKey(key)
    try {
      if (action.type === 'navigate' && typeof action.payload.path === 'string') {
        setDismissedActions(prev => new Set(prev).add(key))
        goToPath(action.payload.path)
        return
      }

      if (action.type === 'createLead') {
        const customerName = String(action.payload.customerName || '').trim() || 'New lead'
        await createLead({
          customerName,
          email: String(action.payload.email || '').trim(),
          phone: String(action.payload.phone || '').trim(),
          company: String(action.payload.companyName || '').trim(),
          status: 'new',
          assignedRepId: 0,
          location: '',
          financingStatus: '',
          timeFrame: '',
          vehicleInterest: '',
          truckIds: [],
          truckRequest: {},
          followUpNotes: action.payload.notes
            ? [
                {
                  id: `assistant-${Date.now()}`,
                  text: String(action.payload.notes),
                  author: 'assistant',
                  createdAt: new Date().toISOString(),
                },
              ]
            : [],
          applicationIds: [],
          lastContactDate: null,
          nextFollowUpDate: null,
          source: 'portal-assistant',
          priority: action.payload.priority || 'medium',
          tags: ['assistant'],
          createdBy: '',
          lastActionDate: null,
        })
        setDismissedActions(prev => new Set(prev).add(key))
        enqueueSnackbar('Lead created', { variant: 'success' })
        goToPath('/admin/lead-management')
        return
      }

      if (action.type === 'updateLeadStatus') {
        const status = action.payload.status as LeadStatus
        await updateLead({
          id: action.payload.leadId,
          updates: { status },
          statusChangeNote: 'Updated via portal assistant',
        })
        setDismissedActions(prev => new Set(prev).add(key))
        enqueueSnackbar(`Lead status → ${status}`, { variant: 'success' })
        return
      }

      if (action.type === 'updateInventoryStatus') {
        await updateDocumentAsync({
          id: action.payload.vehicleId,
          data: { status: action.payload.status },
        })
        setDismissedActions(prev => new Set(prev).add(key))
        enqueueSnackbar(`Inventory status → ${action.payload.status}`, { variant: 'success' })
        if (action.payload.status === 'Sold' || action.payload.status === 'Pending Sale') {
          enqueueSnackbar('Open the truck if buyer details still need filling', {
            variant: 'info',
          })
          goToPath(inventoryV3Href({ open: 'edit', truckId: action.payload.vehicleId }))
        }
        return
      }

      if (action.type === 'updateInventoryFields') {
        const data: Record<string, unknown> = {}
        const tierField = action.payload.fields.find(field => field.field === 'tier')
        if (tierField) {
          const snap = await getDoc(doc(db, 'master_inventory', action.payload.vehicleId))
          const current = snap.exists() ? snap.get('tier') : null
          const reason = vehicleTierRaiseError(current, tierField.to)
          if (reason) {
            enqueueSnackbar(reason, { variant: 'warning' })
            return
          }
        }
        for (const field of action.payload.fields) {
          data[field.field] = field.field === 'tier' ? canonicalVehicleTier(field.to) : field.to
        }
        await updateDocumentAsync({
          id: action.payload.vehicleId,
          data,
        })
        setDismissedActions(prev => new Set(prev).add(key))
        enqueueSnackbar('Inventory fields saved', { variant: 'success' })
        return
      }

      if (action.type === 'applyGraybookPrice') {
        const data: Record<string, unknown> = {
          optic_list_price: action.payload.retail,
          grayBookRetailPrice: action.payload.retail,
        }
        if (action.payload.wholesale != null) {
          data.grayBookWholeSalePrice = action.payload.wholesale
        }
        await updateDocumentAsync({
          id: action.payload.vehicleId,
          data,
        })
        setDismissedActions(prev => new Set(prev).add(key))
        enqueueSnackbar(
          `List price set to $${Number(action.payload.retail).toLocaleString('en-US')}`,
          { variant: 'success' },
        )
        return
      }

      if (action.type === 'createInventoryList') {
        await new Promise<void>((resolve, reject) => {
          createList(
            {
              name: action.payload.name,
              description: action.payload.description,
              initialInventoryIds: action.payload.inventoryIds,
            },
            { onSuccess: () => resolve(), onError: error => reject(error) },
          )
        })
        setDismissedActions(prev => new Set(prev).add(key))
        enqueueSnackbar('Inventory list created', { variant: 'success' })
        goToPath('/admin/list-builder')
        return
      }

      if (action.type === 'createMarketingCampaign') {
        await createCampaign({
          name: action.payload.name,
          subject: action.payload.subject,
          htmlBody: action.payload.htmlBody,
          featuredVehicleIds: action.payload.featuredVehicleIds,
          audienceFilter: action.payload.audienceFilter as unknown as MarketingCampaign['audienceFilter'],
        })
        setDismissedActions(prev => new Set(prev).add(key))
        enqueueSnackbar('Campaign draft saved', { variant: 'success' })
        goToPath('/admin/lead-management')
        return
      }

      if (action.type === 'sendMarketingCampaign') {
        await sendCampaign(action.payload.campaignId)
        setDismissedActions(prev => new Set(prev).add(key))
        enqueueSnackbar('Campaign send started', { variant: 'success' })
        return
      }

      if (action.type === 'createRoutine') {
        if (!activeProfileId) {
          enqueueSnackbar('Open a named assistant to save a routine', { variant: 'warning' })
          return
        }
        await createAssistantRoutine(activeProfileId, {
          kind: action.payload.kind,
          name: action.payload.prompt.replace(/\s+/g, ' ').trim().slice(0, 60) || 'Routine',
          schedule: action.payload.schedule,
          times: action.payload.times,
          days: action.payload.days,
          date: action.payload.date,
          recipientUserIds: action.payload.recipientUserIds,
          recipientEmails: action.payload.recipientEmails,
          prompt: action.payload.prompt,
          reportTopic: action.payload.kind === 'report' ? action.payload.reportTopic : null,
        })
        setDismissedActions(prev => new Set(prev).add(key))
        enqueueSnackbar('Routine saved. It emails this assistant’s members.', { variant: 'success' })
        return
      }

      if (action.type === 'runRoutine') {
        await runAssistantRoutineNow(action.payload.routineId)
        setDismissedActions(prev => new Set(prev).add(key))
        enqueueSnackbar('Test email sent', { variant: 'success' })
        return
      }

      if (action.type === 'clockIn') {
        await startTimer({
          date: DateTime.now().toISODate(),
          startTime: DateTime.now().toISO(),
          duration: 0,
          userId: '',
          timerActive: true,
        })
        setDismissedActions(prev => new Set(prev).add(key))
        enqueueSnackbar('Clocked in', { variant: 'success' })
      }
    } catch (err) {
      console.error('assistant action failed', err)
      enqueueSnackbar('Could not apply that change', { variant: 'error' })
    } finally {
      setBusyActionKey(null)
    }
  }

  const desktopTop = `calc(${CHROME_GAP}px + ${SAFE_AREA_TOP} + ${APPBAR_HEIGHT_DESKTOP}px + ${CHROME_GAP}px)`
  const workspacePaper = {
    width: '100vw',
    maxWidth: '100vw',
    height: '100dvh',
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
    borderRadius: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    pt: SAFE_AREA_TOP,
    overflow: 'hidden',
  }
  const paperSx = workspace
    ? workspacePaper
    : isMobile
    ? {
        width: '100vw',
        maxWidth: '100vw',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column' as const,
        pt: SAFE_AREA_TOP,
        pb: SAFE_AREA_BOTTOM,
      }
    : {
        width: assistantWidth + (historyOpen && !isMobile ? HISTORY_WIDTH : 0),
        maxWidth: `calc(100vw - ${CHROME_GAP * 2}px)`,
        top: desktopTop,
        right: CHROME_GAP,
        bottom: CHROME_GAP,
        height: 'auto',
        borderRadius: `${CHROME_RADIUS}px`,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 10px 28px rgba(15, 23, 42, 0.10)',
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden',
        transition: isResizing
          ? 'none'
          : theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shortest,
            }),
      }

  return (
    <Drawer
      anchor='right'
      open={assistant.isOpen}
      onClose={assistant.closeAssistant}
      variant={isMobile ? 'temporary' : 'persistent'}
      hideBackdrop={!isMobile}
      ModalProps={isMobile ? { keepMounted: true } : undefined}
      sx={{
        zIndex: theme => (workspace ? theme.zIndex.modal : theme.zIndex.drawer),
        pointerEvents: assistant.isOpen ? 'auto' : 'none',
        '& .MuiDrawer-paper': {
          ...paperSx,
          pointerEvents: 'auto',
        },
      }}
      PaperProps={{
        elevation: isMobile ? 16 : 0,
      }}
    >
      {!isMobile && !workspace ? (
        <Box
          role='separator'
          aria-orientation='vertical'
          aria-label='Resize assistant'
          aria-valuemin={ASSISTANT_MIN_WIDTH}
          aria-valuemax={ASSISTANT_MAX_WIDTH}
          aria-valuenow={assistantWidth}
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={endResize}
          onPointerCancel={endResize}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 10,
            cursor: 'col-resize',
            zIndex: 2,
            touchAction: 'none',
            userSelect: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::after': {
              content: '""',
              width: 3,
              height: 48,
              borderRadius: 1.5,
              bgcolor: isResizing ? 'primary.main' : 'divider',
              opacity: isResizing ? 1 : 0.7,
              transition: theme.transitions.create(['background-color', 'opacity', 'height'], {
                duration: theme.transitions.duration.shortest,
              }),
            },
            '&:hover::after': {
              bgcolor: 'primary.main',
              opacity: 1,
              height: 64,
            },
          }}
        />
      ) : null}

      <Box
        data-assistant-demo-anchor
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 0.25 : 0.5,
          px: isMobile ? 1 : 1.5,
          py: isMobile ? 0.75 : 1.25,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
          minHeight: isMobile ? 40 : TOUCH_MIN,
        }}
      >
        {workspace ? (
          <Button
            color='inherit'
            startIcon={<ArrowBack />}
            onClick={leaveToPortal}
            aria-label='Back to portal'
            data-assistant-demo='portal'
            sx={{ minHeight: 44, px: 1, flexShrink: 0 }}
          >
            Portal
          </Button>
        ) : null}
        <Tooltip title={historyOpen ? 'Hide chats' : 'Chats'}>
          <IconButton
            size='small'
            aria-label={historyOpen ? 'Hide chats' : 'Show chats'}
            aria-expanded={historyOpen}
            data-assistant-demo='chats'
            onClick={() => setHistoryOpen(open => !open)}
            sx={{ width: isMobile ? 40 : TOUCH_MIN, height: isMobile ? 40 : TOUCH_MIN }}
          >
            <ViewSidebarOutlined fontSize='small' />
          </IconButton>
        </Tooltip>
        {workspace && isMobile ? null : (
          <AutoAwesome sx={{ fontSize: isMobile ? 18 : 20 }} color='primary' />
        )}
        <Typography
          variant={isMobile ? 'subtitle2' : 'subtitle1'}
          sx={{ fontWeight: 600, minWidth: 0, fontSize: isMobile ? '0.875rem' : undefined }}
          noWrap
        >
          {activeProfile?.name || (workspace && isMobile ? 'Assistant' : 'Portal assistant')}
        </Typography>
        {workspace && isMobile ? null : (
        <Chip
          label='Beta'
          size='small'
          color='primary'
          variant='outlined'
          sx={{
            height: isMobile ? 18 : 20,
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: '0.04em',
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        )}
        {activeProfile ? (
          <Button
            size='small'
            color='inherit'
            onClick={() => setProfileDialog(activeProfile.id)}
            sx={{ textTransform: 'none', minHeight: 32, flexShrink: 0 }}
          >
            Details
          </Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {workspace && workHidden ? (
          <Button
            size='small'
            color='inherit'
            onClick={() => setWorkHidden(false)}
            sx={{ textTransform: 'none', minHeight: 32, flexShrink: 0 }}
          >
            Work
          </Button>
        ) : null}
        <Tooltip title={workspace ? 'Assistant mode on' : 'Assistant mode'}>
          <IconButton
            size='small'
            aria-label='Assistant mode'
            aria-pressed={workspace}
            data-assistant-demo='mode'
            color={workspace ? 'primary' : 'inherit'}
            onClick={openWorkspace}
            sx={{ width: isMobile ? 40 : TOUCH_MIN, height: isMobile ? 40 : TOUCH_MIN }}
          >
            <OpenInFull fontSize='small' />
          </IconButton>
        </Tooltip>
        <AssistantShareButton
          title={threads.find(thread => thread.id === chatId)?.title || 'Portal assistant'}
          messages={messages}
        />
        <AssistantToolEditorButton />
        <AssistantSettingsPopover
          effort={effort}
          onEffortChange={setEffort}
          model={model}
          onModelChange={setModel}
          closeAfterNavigate={closeAfterNavigate}
          onCloseAfterNavigateChange={setCloseAfterNavigate}
          learnFromChats={learnFromChats}
          onLearnFromChatsChange={setLearnFromChats}
          toolPrefs={toolPrefs}
          onToolPrefsChange={handleToolPrefsChange}
          debugMode={debugMode}
          onDebugModeChange={setDebugMode}
          disabled={streaming}
          onClear={() => {
            clear()
            setDismissedActions(new Set())
          }}
        />
        <IconButton
          size='small'
          aria-label='Close assistant'
          onClick={assistant.closeAssistant}
          sx={{ width: isMobile ? 40 : TOUCH_MIN, height: isMobile ? 40 : TOUCH_MIN }}
        >
          <Close fontSize={isMobile ? 'small' : 'medium'} />
        </IconButton>
      </Box>

      {workspace && isMobile ? (
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={phonePane}
          aria-label='Assistant pane'
          onChange={(_, value: 'work' | 'chat' | null) => {
            if (!value) return
            stickChatRef.current = value === 'chat'
            setPhonePane(value)
          }}
          sx={{ px: 1, py: 0.5, flexShrink: 0 }}
        >
          <ToggleButton value='work' sx={{ minHeight: 44, textTransform: 'none' }}>
            Work
          </ToggleButton>
          <ToggleButton value='chat' sx={{ minHeight: 44, textTransform: 'none' }}>
            Chat
          </ToggleButton>
        </ToggleButtonGroup>
      ) : null}

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {historyOpen ? (
          <AssistantHistoryPane
            tab={historyTab}
            onTab={setHistoryTab}
            threads={threads}
            activeId={chatId}
            streamingIds={streamingChatIds}
            loading={threadsLoading}
            profiles={profiles}
            profilesLoading={profilesLoading}
            profilesError={profilesError}
            activeProfileId={activeProfileId}
            onNew={() => {
              setActiveProfileId(null)
              newChat()
              if (isMobile) setHistoryOpen(false)
            }}
            onOpen={id => {
              setActiveProfileId(null)
              openChat(id)
              if (isMobile) setHistoryOpen(false)
            }}
            onNewAssistant={() => setProfileDialog('create')}
            onOpenAssistant={id => {
              const profile = profiles.find(item => item.id === id)
              if (!profile?.chatId) return
              setActiveProfileId(id)
              openChat(profile.chatId)
              if (isMobile) setHistoryOpen(false)
            }}
            onEditAssistant={id => setProfileDialog(id)}
            onRename={renameChat}
            onDelete={deleteChat}
          />
        ) : null}
        {showThread && !(isMobile && historyOpen) ? (
      <Box
        sx={{
          flex: workspace && !isMobile && !workHidden ? `0 0 ${workspaceChatPx}px` : 1,
          width: workspace && !isMobile && !workHidden ? workspaceChatPx : undefined,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      <Box
        data-assistant-demo='thread'
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: isMobile ? 1.25 : 2,
          py: isMobile ? 1 : 1.5,
          minHeight: 0,
        }}
      >
        {demoSample === 'thread' ? <DemoPreviewSample /> : null}
        {transcriptLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1 : 1.5 }}>
            <Skeleton variant='rounded' height={56} sx={{ alignSelf: 'flex-end', width: '55%', borderRadius: 1.5 }} />
            <Skeleton variant='rounded' height={120} sx={{ width: '90%', borderRadius: 1.5 }} />
            <Skeleton variant='rounded' height={72} sx={{ alignSelf: 'flex-end', width: '40%', borderRadius: 1.5 }} />
            <Skeleton variant='rounded' height={160} sx={{ width: '90%', borderRadius: 1.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <CircularProgress size={14} />
              <Typography variant='caption' color='text.secondary'>
                Loading chat…
              </Typography>
            </Box>
          </Box>
        ) : null}

        {!transcriptLoading && messages.length === 0 ? (
          <Box>
            <Typography
              variant={isMobile ? 'caption' : 'body2'}
              color='text.secondary'
              sx={{
                mb: isMobile ? 1 : 1.5,
                display: 'block',
                fontSize: isMobile ? '0.8125rem' : undefined,
                lineHeight: isMobile ? 1.4 : undefined,
              }}
            >
              {workspace
                ? 'Ask for Gray Book prices, inventory, or leads. Results open on the work side. Edits still need your Yes.'
                : 'Ask for Gray Book prices, inventory, or leads. Edits and status moves need your Yes — they save as you, not a bot.'}
            </Typography>
            {workspace && showWorkStage ? null : (
            <Stack spacing={workspace ? 1.5 : 0}>
              {workspace ? (
                WORKSPACE_STARTERS.map(group => (
                  <Box key={group.group}>
                    <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
                      {group.group}
                    </Typography>
                    <Stack direction='row' flexWrap='wrap' gap={1} sx={{ mt: 0.5 }}>
                      {group.prompts.map(prompt => (
                        <Button
                          key={prompt}
                          size='small'
                          variant='outlined'
                          disabled={streaming}
                          onClick={() => void send(prompt)}
                          sx={{ minHeight: TOUCH_MIN, textTransform: 'none' }}
                        >
                          {prompt}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                ))
              ) : (
                <Stack direction='row' flexWrap='wrap' gap={isMobile ? 0.75 : 1}>
                  {SUGGESTIONS.map(prompt => (
                    <Chip
                      key={prompt}
                      label={prompt}
                      size='small'
                      onClick={() => {
                        if (streaming) return
                        void send(prompt)
                      }}
                      sx={{
                        cursor: streaming ? 'default' : 'pointer',
                        minHeight: isMobile ? 32 : TOUCH_MIN,
                        height: isMobile ? 32 : undefined,
                        fontSize: isMobile ? '0.75rem' : undefined,
                        opacity: streaming ? 0.5 : 1,
                        pointerEvents: streaming ? 'none' : 'auto',
                        '& .MuiChip-label': isMobile ? { px: 1 } : undefined,
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
            )}
          </Box>
        ) : null}

        {!transcriptLoading
          ? messages.map((message, index) => {
          const prior = messages[index - 1]
          const routineReply =
            message.role === 'assistant' &&
            Boolean(message.content.trim()) &&
            prior?.role === 'user' &&
            /\broutines?\b/i.test(prior.content)
          const isStreamingPlaceholder =
            streaming &&
            message.role === 'assistant' &&
            message === messages[messages.length - 1] &&
            !message.content
          return (
            <Box
              key={message.id}
              sx={{
                mb: isMobile ? 1 : 1.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <Box
                sx={{
                  maxWidth:
                    workspace && !isMobile
                      ? '100%'
                      : isWideLayout
                        ? '720px'
                        : isMobile
                          ? '100%'
                          : '90%',
                  width:
                    message.role === 'assistant' && (isWideLayout || isMobile)
                      ? '100%'
                      : 'auto',
                  px: isMobile ? 1 : 1.25,
                  py: isMobile ? 0.75 : 1,
                  borderRadius: 1.5,
                  bgcolor: message.role === 'user' ? 'primary.main' : 'action.hover',
                  color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  wordBreak: 'break-word',
                }}
              >
                {message.role === 'user' && message.authorName ? (
                  <Typography
                    variant='caption'
                    sx={{ display: 'block', opacity: 0.85, mb: 0.25, fontSize: '0.7rem' }}
                  >
                    {message.authorName}
                  </Typography>
                ) : null}
                {message.role === 'assistant' ? (
                  isStreamingPlaceholder ? (
                    toolSteps.length ? (
                      <AssistantToolStatus steps={toolSteps} />
                    ) : (
                      <Typography
                        variant={isMobile ? 'caption' : 'body2'}
                        sx={{ fontSize: isMobile ? '0.8125rem' : undefined }}
                      >
                        …
                      </Typography>
                    )
                  ) : (
                    <>
                      <AssistantMarkdown
                        content={message.content}
                        onNavigate={goToPath}
                        routes={routes}
                        dense={isMobile}
                      />
                      {message.failed && message.id === messages[messages.length - 1]?.id && !streaming ? (
                        <Button
                          size='small'
                          variant='outlined'
                          onClick={() => retry()}
                          sx={{ mt: 1, minHeight: isMobile ? 32 : TOUCH_MIN - 8 }}
                        >
                          Retry
                        </Button>
                      ) : null}
                      {message.content.trim() ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.25 }}>
                          {routineReply && !isStreamingPlaceholder ? (
                            <Button
                              size='small'
                              variant='outlined'
                              color='inherit'
                              onClick={() => {
                                if (!activeProfileId) {
                                  enqueueSnackbar('Open a named assistant to make a routine', { variant: 'warning' })
                                  return
                                }
                                setRoutineDraft(routineComposerDraft(messages, index))
                                setRoutineComposer(true)
                                setProfileDialog(activeProfileId)
                              }}
                              sx={{
                                minHeight: isMobile ? 28 : 32,
                                px: 1,
                                py: 0,
                                fontSize: '0.75rem',
                                lineHeight: 1.2,
                                textTransform: 'none',
                                color: 'text.secondary',
                                borderColor: 'divider',
                              }}
                            >
                              Make routine
                            </Button>
                          ) : null}
                          <Tooltip title='Copy answer'>
                            <IconButton
                              size='small'
                              aria-label='Copy answer'
                              onClick={() => void handleCopy(message.content)}
                              sx={{ width: isMobile ? 28 : 32, height: isMobile ? 28 : 32 }}
                            >
                              <ContentCopy sx={{ fontSize: isMobile ? 14 : 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : null}
                      {debugMode && message.scanNotes?.length ? (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant='caption' sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                            Debug
                          </Typography>
                          {message.scanNotes.map((note, index) => (
                            <Box key={`${note.startDate}-${note.endDate}-${note.maxRecords}-${index}`} sx={{ mb: 0.5 }}>
                              {scanDebugLines(note).map(line => (
                                <Typography key={line} variant='caption' display='block' color='text.secondary'>
                                  {line}
                                </Typography>
                              ))}
                            </Box>
                          ))}
                          <Button
                            size='small'
                            onClick={() => setDebugMode(false)}
                            sx={{ mt: 0.5, px: 0, minHeight: 32, textTransform: 'none' }}
                          >
                            Disable debug mode. I don&apos;t want to see this extra detail.
                          </Button>
                        </Box>
                      ) : null}
                    </>
                  )
                ) : (
                  <Typography
                    variant={isMobile ? 'caption' : 'body2'}
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontSize: isMobile ? '0.8125rem' : undefined,
                      lineHeight: isMobile ? 1.4 : undefined,
                    }}
                  >
                    {message.content}
                  </Typography>
                )}
                {hideStageExtras ? (
                  <AssistantMessageExtras
                    message={message}
                    dense={isMobile}
                    dismissedActions={dismissedActions}
                    busyActionKey={busyActionKey}
                    actionKey={actionKey}
                    onConfirm={(action, messageId, index) =>
                      void handleConfirm(action, messageId, index)
                    }
                    onDismiss={key => setDismissedActions(prev => new Set(prev).add(key))}
                    onPickChartType={handleChartTypePick}
                    onOpenPath={goToPath}
                    onSuggest={prompt => {
                      if (streaming) return
                      void send(prompt)
                    }}
                    suggestDisabled={streaming}
                    actions='routine'
                  />
                ) : (
                  <AssistantMessageExtras
                    message={message}
                    dense={isMobile}
                    dismissedActions={dismissedActions}
                    busyActionKey={busyActionKey}
                    actionKey={actionKey}
                    onConfirm={(action, messageId, index) =>
                      void handleConfirm(action, messageId, index)
                    }
                    onDismiss={key => setDismissedActions(prev => new Set(prev).add(key))}
                    onPickChartType={handleChartTypePick}
                    onOpenPath={goToPath}
                    onSuggest={prompt => {
                      if (streaming) return
                      void send(prompt)
                    }}
                    suggestDisabled={streaming}
                    isChartPinned={index =>
                      pinnedChartIds.includes(chartPinId(message.id, index))
                    }
                    onToggleChartPin={index => toggleChartPin(chartPinId(message.id, index))}
                  />
                )}
              </Box>
            </Box>
          )
        })
          : null}
        {!transcriptLoading && streaming && toolSteps.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CircularProgress size={14} />
            <Typography variant='caption' color='text.secondary'>
              Thinking…
            </Typography>
          </Box>
        ) : null}
        {!transcriptLoading &&
        streaming &&
        toolSteps.length > 0 &&
        messages[messages.length - 1]?.content ? (
          <Box sx={{ mb: 1 }}>
            <AssistantToolStatus steps={toolSteps} />
          </Box>
        ) : null}
        {error ? (
          <Box
            sx={{
              mb: 1,
              p: 1.25,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'error.light',
              bgcolor: 'error.main',
              color: 'error.contrastText',
              opacity: 0.92,
            }}
          >
            <Typography variant='body2' sx={{ mb: 1 }}>
              {error}
            </Typography>
            <Stack direction='row' spacing={1}>
              {canRetry ? (
                <Button
                  size='small'
                  variant='contained'
                  color='inherit'
                  onClick={() => retry()}
                  sx={{ minHeight: TOUCH_MIN - 8 }}
                >
                  Retry
                </Button>
              ) : null}
              <Button
                size='small'
                variant='outlined'
                color='inherit'
                onClick={dismissError}
                sx={{ minHeight: TOUCH_MIN - 8, borderColor: 'currentColor' }}
              >
                Dismiss
              </Button>
            </Stack>
          </Box>
        ) : null}
        <div ref={bottomRef} />
      </Box>
      </Box>
        ) : null}
        {workspace && !isMobile && showThread && showWorkStage && !(isMobile && historyOpen) ? (
          <Box
            role='separator'
            aria-orientation='vertical'
            aria-label='Resize chat and charts'
            aria-valuemin={WORKSPACE_CHAT_MIN}
            aria-valuenow={workspaceChatPx}
            onPointerDown={handleSplitPointerDown}
            onPointerMove={handleSplitPointerMove}
            onPointerUp={endSplitResize}
            onPointerCancel={endSplitResize}
            sx={{
              width: 10,
              flexShrink: 0,
              cursor: 'col-resize',
              touchAction: 'none',
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&::after': {
                content: '""',
                width: 3,
                height: 48,
                borderRadius: 1.5,
                bgcolor: splitResizing ? 'primary.main' : 'divider',
                opacity: splitResizing ? 1 : 0.7,
              },
              '&:hover::after': {
                bgcolor: 'primary.main',
                opacity: 1,
                height: 64,
              },
            }}
          />
        ) : null}
        {showWorkStage && !(isMobile && historyOpen) ? (
          <Box
            data-assistant-demo='stage'
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <AssistantWorkStage
              pinned={stageLists.pinned}
              history={stageLists.history}
              tallCharts={!isMobile}
              streaming={streaming}
              dismissedActions={dismissedActions}
              busyActionKey={busyActionKey}
              actionKey={actionKey}
              onConfirm={(action, messageId, index) => void handleConfirm(action, messageId, index)}
              onDismiss={key => setDismissedActions(prev => new Set(prev).add(key))}
              onPickChartType={handleChartTypePick}
              onOpenPath={goToPath}
              onSuggest={prompt => {
                if (streaming) return
                void send(prompt)
              }}
              onTogglePin={toggleStagePin}
              onRemove={forgetStageMessage}
              onClear={clearStageHistory}
              onHide={() => {
                if (isMobile) {
                  stickChatRef.current = true
                  setPhonePane('chat')
                  return
                }
                setWorkHidden(true)
              }}
              showDemoPreview={demoSample === 'stage'}
              isChartPinned={(messageId, index) =>
                pinnedChartIds.includes(chartPinId(messageId, index))
              }
              onToggleChartPin={(messageId, index) =>
                toggleChartPin(chartPinId(messageId, index))
              }
            />
          </Box>
        ) : null}
      </Box>

      <Box
        data-assistant-demo='composer'
        sx={{
          p: isMobile ? 1 : 1.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: isMobile ? 0.75 : 1,
          flexShrink: 0,
          pb: isMobile ? `calc(8px + ${SAFE_AREA_BOTTOM})` : workspace ? 1.5 : 1.5,
        }}
      >
        <TextField
          size='small'
          fullWidth
          placeholder={streaming ? 'Type while waiting…' : 'Ask the portal…'}
          value={draft}
          onChange={event => setDraft(event.target.value)}
          onKeyDown={handleComposerKeyDown}
          inputProps={{
            style: {
              // Keep ≥16px on phone so iOS does not zoom the field.
              minHeight: isMobile ? 24 : TOUCH_MIN - 16,
              fontSize: 16,
            },
          }}
        />
        {streaming ? (
          <IconButton
            color='error'
            aria-label='Stop'
            onClick={cancel}
            sx={{ width: isMobile ? 40 : TOUCH_MIN, height: isMobile ? 40 : TOUCH_MIN }}
          >
            <Stop fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>
        ) : (
          <IconButton
            color='primary'
            aria-label='Send'
            disabled={!draft.trim()}
            onClick={handleSend}
            sx={{ width: isMobile ? 40 : TOUCH_MIN, height: isMobile ? 40 : TOUCH_MIN }}
          >
            <Send fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>
        )}
      </Box>
      <AssistantProfileDialog
        open={profileDialog != null}
        profile={
          profileDialog && profileDialog !== 'create'
            ? profiles.find(item => item.id === profileDialog) ?? null
            : null
        }
        uid={assistantUid ?? ''}
        startComposer={routineComposer}
        composerDraft={routineDraft}
        onClose={() => {
          setProfileDialog(null)
          setRoutineComposer(false)
          setRoutineDraft(null)
        }}
        onCreated={({ id, chatId: createdChatId }) => {
          setProfileDialog(null)
          setHistoryTab('assistants')
          setHistoryOpen(true)
          setActiveProfileId(id)
          openChat(createdChatId)
        }}
        onDeleted={() => {
          const removedId = profileDialog
          setProfileDialog(null)
          if (removedId && removedId === activeProfileId) {
            setActiveProfileId(null)
            newChat()
          }
        }}
      />
    </Drawer>
  )
}
